//
// SheetMap is used by Minimap in order to display a sheet in an own viewer instance - instead of a pre-rendered image containg the automatically generated map.
//

import AecData from '../common/AecModelData.js';

class SheetMapConfig {
    constructor(viewport, mapMatrix, levelGuid) {
        // {object}: viewport on the sheet, as obtained from AecModelData
        this.viewport = viewport;

        // {Matrix4} transforms from 3D world coords to sheetmap coords in [-1,1]^2
        this.mapMatrix = mapMatrix;
        this.mapMatrixInv = mapMatrix.clone().invert();

        // {string}
        this.levelGuid = levelGuid;

        // {SheetMapThumbnail}
        this.thumbnail = undefined;
    }

    async getSheetNode() {
        if (!this._sheetNode) {
            this._sheetNode = await this.viewport.getParentSheetNode();
        }

        return this._sheetNode;
    }
}

export default class SheetMap {

    // Computes a mapMatrix based on a viewport on a sheet. The matrix transforms from 3D model coordinates to
    // normalized map coords in [-1,1]^2.
    //  @param {Object} viewport - Viewport data obtained from AECModelData.json
    //  @param {av.Model} model3D  - A currently visible 3D model of the Revit document for which we found the sheet.
    //  @returns THREE.Matrix4
    static computeMapMatrix(viewport, model3D) {

        // We map from model coords via sheet coords to finally [-1,1]^2.
        //
        // The functions getViewportBounds() and get3DTo2DMatrix() work in sheet-space, so that they require
        // the sheetUnitScale - which we don't know before loading the sheet.
        //
        // But, since we finally map the viewport to [-1,1] anyway, a uniform scaling of the sheet has no impact on
        // the final map matrix anyway. So we can assume a dummy unitScale of 1.0 here.
        const DummyUnitScale = 1.0;

        let sheetRegion = viewport.getViewportBounds(DummyUnitScale);

        // Create matrix to map sheetRegion to [-1,1]^2
        let sheetToMap = AecData.remapRectangle(
            sheetRegion.min.x, sheetRegion.min.y, sheetRegion.max.x, sheetRegion.max.y, // from: SheetRegion
            -1, -1, 1, 1                                                                // to: [0,1]^2
        );

        let modelToSheet = viewport.get3DTo2DMatrix(DummyUnitScale);
        let mapMatrix = new THREE.Matrix4().multiplyMatrices(sheetToMap, modelToSheet);

        if (viewport.isMatrixInLocalCoords()) {
            // The mapMatrix computed so far maps local model coordinates to map coordinates.
            // Finally, we want to map from local LMV coordinates. Therefore, the final transform
            // must revert any load-time transforms first.
            const modelTransform = model3D.getInverseModelToViewerTransform();

            if (modelTransform) {
                mapMatrix.multiply(modelTransform);
            }
        }

        return mapMatrix;
    }

    // Creates sheetMap configurations for all sheetMaps that are found on the current floor.
    //  @param {av.Model[]} models - 3D models in which we search for viewports/sheets
    //  @param {string}     levelGuid
    //  @returns {SheetMapConfig[]}
    static createSheetMapConfigs(models3D, levelGuid, viewportsExtension, useOnlyAecModelDataViewports) {

        // Find all viewports that would be suitable as a floor map.
        const maps = [];

        for (let i = 0; i < models3D.length; i++) {

            // Find viewports for levelGuid in the document of mapModel[i]
            const model3D = models3D[i];
            const bubbleNode = model3D.getDocumentNode();

            if (!bubbleNode) {
                continue;
            }

            let modelViewports = viewportsExtension.findViewportsInLevel(model3D, levelGuid);

            if (useOnlyAecModelDataViewports) {
                modelViewports = modelViewports.filter(viewport => viewport instanceof Autodesk.AEC.ViewportsExtension.AecModelDataViewport);
            }

            // For each viewport...
            for (let j=0; j<modelViewports.length; j++) {

                const viewport = modelViewports[j];

                // Compute mapMatrix for this viewport
                let mapMatrix = SheetMap.computeMapMatrix(viewport, model3D);

                // We found a candidate (viewport, sheet) pair
                const map = new SheetMapConfig(viewport, mapMatrix, levelGuid);

                maps.push(map);
            }
        }

        return maps;
    }
}
